<template>
  <v-dialog v-model="show" persistent max-width="1000px">
    <template
      v-for="(_, scopedSlotName) in $scopedSlots"
      #[scopedSlotName]="slotData"
    >
      <slot :name="scopedSlotName" v-bind="slotData" />
    </template>
    <template v-for="(_, slotName) in $slots" #[slotName]>
      <slot :name="slotName" />
    </template>
    <v-card :loading="isLoading" :disabled="isLoading">
      <v-card-title>Update Invoice(s)</v-card-title>
      <v-card-text>
        <v-alert text color="warning" icon="mdi-information-outline" dense>
          These invoice(s) will be updated:
          <ul>
            <li v-for="(invoice, i) in invoices" :key="i">
              <strong>{{ invoice.code }}</strong>
            </li>
          </ul>
        </v-alert>
        <v-form ref="form" class="mt-4" :disabled="isLoading">
          <v-row align="center">
            <v-col cols="12" md="3">
              <div class="label mb-2">Status</div>
              <v-select
                v-model="form.status"
                :items="statusOptions"
                :rules="[validationRules.required]"
                solo
                flat
                outlined
                dense
                placeholder="Select invoice status"
                @change="changeInvoiceStatus"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-slide-x-transition hide-on-leave>
                <div v-if="isShowPaidDate">
                  <div class="label mb-2">Paid Date</div>
                  <v-menu
                    ref="paidDateMenu"
                    v-model="paidDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.paid_date"
                        :rules="[paidDateValidationRules]"
                        placeholder="Select paid date"
                        prepend-icon="mdi-calendar"
                        solo
                        flat
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.paid_date"
                      @change="$refs.paidDateMenu.save(form.paid_date)"
                    ></v-date-picker>
                  </v-menu>
                </div>
              </v-slide-x-transition>
              <v-slide-x-transition hide-on-leave>
                <div v-if="isShowCanceledDate">
                  <div class="label mb-2">Canceled Date</div>
                  <v-menu
                    ref="canceledDateMenu"
                    v-model="canceledDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template #activator="{ on, attrs }">
                      <v-text-field
                        v-model="form.canceled_date"
                        :rules="[canceledDateValidationRules]"
                        placeholder="Select canceled date"
                        prepend-icon="mdi-calendar"
                        solo
                        flat
                        outlined
                        dense
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="form.canceled_date"
                      @change="$refs.canceledDateMenu.save(form.canceled_date)"
                    ></v-date-picker>
                  </v-menu>
                </div>
              </v-slide-x-transition>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="justify-end">
        <v-btn depressed outlined @click="show = false">Cancel</v-btn>
        <v-btn depressed color="primary" @click="clickSubmit">Submit</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import constant from '@/constant'

export default {
  name: 'InvoiceUpdateBulkDialog',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    invoices: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      paidDateMenu: false,
      canceledDateMenu: false,
      form: {
        status: null,
        paid_date: null,
        canceled_date: null,
      },
    }
  },
  computed: {
    ...mapGetters({ isLoading: 'invoice/isUpdateBulkLoading' }),
    show: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
    isShowPaidDate() {
      return ['PAID'].includes(this.form.status)
    },
    isShowCanceledDate() {
      return ['CANCELED'].includes(this.form.status)
    },
    statusOptions() {
      return constant.invoiceStatusOptions
    },
    paidDateValidationRules() {
      if (this.form.status === 'PAID') {
        return (v) => !!v || 'Paid date is required'
      }
      return () => true
    },
    canceledDateValidationRules() {
      if (this.form.status === 'CANCELED') {
        return (v) => !!v || 'Canceled date is required'
      }
      return () => true
    },
  },
  methods: {
    ...mapActions({ updateBulk: 'invoice/updateBulk' }),
    changeInvoiceStatus(status) {
      if (status === 'PAID') {
        this.form.paid_date = moment().format('YYYY-MM-DD')
        this.form.canceled_date = null
      } else if (status === 'CANCELED') {
        this.form.canceled_date = moment().format('YYYY-MM-DD')
        this.form.paid_date = null
      } else {
        this.form.paid_date = null
        this.form.canceled_date = null
      }
    },
    async clickSubmit() {
      const invoices = this.invoices.map((invoice) => ({
        id: invoice.id,
        ...this.form,
      }))
      await this.updateBulk({ invoices })
      this.show = false
      this.$emit('submit')
    },
  },
}
</script>
